import type { AppConfig, CommonConfig, EtcConfig, ServerCommonConfig } from './config';
import { defaultConfig, PARTITION_KEYS } from './config';
import {
  StorageDriver,
  ElectronDriver,
  WebDriver,
  MemoryDriver,
  chooseInitialDriver,
  pick,
  fillMissingKeysInPlace,
  hydrateSeriesPart,
  serializeSeriesPart,
  splitCommonForSave,
  splitSeriesForSave,
  deepClone,
  reconcileShapeWithDefaultsInPlace,
  isPlainObject,
} from './configDrivers';

type Listener = () => void;
type UnknownRecord = Record<string, unknown>;

function isCompatibleShape(baseValue: unknown, nextValue: unknown): boolean {
  if (isPlainObject(baseValue) !== isPlainObject(nextValue)) return false;
  if (Array.isArray(baseValue) !== Array.isArray(nextValue)) return false;
  if ((baseValue instanceof Map) !== (nextValue instanceof Map)) return false;
  return true;
}

function mergeKnownKeys<T>(base: T, incoming: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(incoming)) {
    return deepClone(base);
  }

  const out: UnknownRecord = deepClone(base as any);
  const src = incoming as UnknownRecord;

  for (const key of Object.keys(out)) {
    if (!Object.prototype.hasOwnProperty.call(src, key)) continue;
    const baseValue = (out as any)[key];
    const nextValue = src[key];

    if (isPlainObject(baseValue) && isPlainObject(nextValue)) {
      (out as any)[key] = mergeKnownKeys(baseValue, nextValue);
      continue;
    }
    if (!isCompatibleShape(baseValue, nextValue)) continue;

    (out as any)[key] = deepClone(nextValue);
  }

  return out as T;
}

function mergeKnownKeysWithFallback<T>(
  schema: T,
  incoming: unknown,
  fallback: T,
): T {
  if (!isPlainObject(schema) || !isPlainObject(fallback)) {
    return deepClone(fallback);
  }

  const out: UnknownRecord = deepClone(schema as any);
  const src = isPlainObject(incoming) ? (incoming as UnknownRecord) : {};
  const fb = fallback as UnknownRecord;

  for (const key of Object.keys(out)) {
    const schemaValue = (out as any)[key];
    const hasIncoming = Object.prototype.hasOwnProperty.call(src, key);
    const incomingValue = hasIncoming ? src[key] : undefined;
    const fallbackValue = fb[key];

    if (isPlainObject(schemaValue)) {
      (out as any)[key] = mergeKnownKeysWithFallback(
        schemaValue,
        hasIncoming ? incomingValue : undefined,
        isPlainObject(fallbackValue) ? fallbackValue : schemaValue,
      );
      continue;
    }

    if (hasIncoming && isCompatibleShape(schemaValue, incomingValue)) {
      (out as any)[key] = deepClone(incomingValue);
      continue;
    }

    if (isCompatibleShape(schemaValue, fallbackValue)) {
      (out as any)[key] = deepClone(fallbackValue);
      continue;
    }

    (out as any)[key] = deepClone(schemaValue);
  }

  return out as T;
}

class ConfigStore {
  // 🔸 configVersion 제거
  private state: AppConfig = { ...defaultConfig };
  private seriesId: string | null = null;

  private driver: StorageDriver = chooseInitialDriver();
  private listeners = new Set<Listener>();

  private commonReady = false;
  private seriesReady = false;

  private dirtyCommon = false;
  private dirtySeries = false;

  constructor() {
    // 앱 시작 시 공용 로드
    this.loadCommon().catch(() => {
      if (this.driver.name === 'electron') {
        this.setDriver(hasLocalStorage() ? WebDriver : MemoryDriver);
        this.loadCommon().catch(() => {/* noop */});
      }
    });
  }

  setDriver(d: StorageDriver) { this.driver = d; }

  /** 외부 구독자(React 훅에서 사용) */
  subscribe(l: Listener) { this.listeners.add(l); return () => this.listeners.delete(l); }
  private emit() { this.listeners.forEach((l) => l()); }

  /** 현재 스냅샷 */
  get(): AppConfig { return this.state; }
  getCommon(): CommonConfig { return splitCommonForSave(this.state) as CommonConfig; }
  getDefaultCommon(): CommonConfig { return splitCommonForSave(defaultConfig) as CommonConfig; }
  getServerCommon(): ServerCommonConfig {
    return pick(this.state as any, PARTITION_KEYS.server as any) as ServerCommonConfig;
  }
  getDefaultServerCommon(): ServerCommonConfig {
    return pick(defaultConfig as any, PARTITION_KEYS.server as any) as ServerCommonConfig;
  }
  async getPersistedServerCommonOrNull(): Promise<ServerCommonConfig | null> {
    try {
      const file = await this.driver.readCommon();
      const fileObj: any = file ?? {};
      const loadedCommon = pick(fileObj as any, PARTITION_KEYS.global);
      if (Object.keys(loadedCommon as any).length === 0) return null;
      const persisted = mergeKnownKeys(this.getDefaultCommon(), loadedCommon);
      return pick(persisted as any, PARTITION_KEYS.server as any) as ServerCommonConfig;
    } catch {
      return null;
    }
  }

  setCommon(patch: Partial<CommonConfig>) {
    this.state = { ...this.state, ...(patch as Partial<AppConfig>) };
    this.dirtyCommon = true;
    this.emit();
    queueMicrotask(() => { this.flushSaves().catch(() => {/* ignore */}); });
  }

  resetCommonToDefault() {
    this.setCommon(this.getDefaultCommon());
  }

  mergeCommonFromServer(serverCommon: unknown) {
    const merged = mergeKnownKeys(this.getCommon(), serverCommon);
    this.setCommon(merged);
  }
  mergeServerCommonFromServer(serverCommon: unknown) {
    const merged = mergeKnownKeys(this.getServerCommon(), serverCommon);
    this.setCommon(merged);
  }
  composeServerCommonForUpload(
    serverCommon: unknown,
    fallback: ServerCommonConfig,
  ): ServerCommonConfig {
    return mergeKnownKeysWithFallback(
      this.getDefaultServerCommon(),
      serverCommon,
      fallback,
    );
  }

  /** 공용 로드/보강(버전체크 제거, 없는 키만 추가) */
  async loadCommon() {
    const file = await this.driver.readCommon();
    const fileObj: any = file ?? {};

    const defaultCommon = pick(defaultConfig as any, PARTITION_KEYS.global);
    const loadedCommon = pick(fileObj as any, PARTITION_KEYS.global);

    const mem = deepClone(loadedCommon);
    reconcileShapeWithDefaultsInPlace(mem, defaultCommon);
    fillMissingKeysInPlace(mem, defaultCommon);

    this.state = { ...this.state, ...(mem as any) };

    // 없는 키가 채워졌으면 저장
    const changed =
      JSON.stringify(loadedCommon) !== JSON.stringify(mem);
    if (changed) {
      await this.driver.writeCommon({ ...fileObj, ...mem });
    }

    this.commonReady = true; this.dirtyCommon = false; this.emit();
  }

  /** 시리즈 전환/로드 + 없는 키만 추가 */
  async setSeriesID(id: string) {
    if (!id) return;
    if (this.seriesId === id && this.seriesReady) return;
    this.seriesId = id;
    this.seriesReady = false;
    await this.loadSeries(id);
  }

  private async loadSeries(id: string) {
    const file = await this.driver.readSeries();
    const fileObj: any = file ?? {};

    const node = (fileObj as any)[id];

    const defaults = pick(defaultConfig as any, PARTITION_KEYS.series);

    if (node) {
      const raw = hydrateSeriesPart(deepClone(node));
      const core = pick(raw, PARTITION_KEYS.series);
      const before = serializeSeriesPart(core);
      reconcileShapeWithDefaultsInPlace(core, defaults);
      fillMissingKeysInPlace(core, defaults);
      const after = serializeSeriesPart(core);

      this.state = { ...this.state, ...(core as any) };

      if (JSON.stringify(before) !== JSON.stringify(after)) {
        await this.driver.writeSeries({
          ...fileObj,
          [id]: after,
        });
      }
    } else {
      const core = { ...defaults } as any;
      await this.driver.writeSeries({
        ...fileObj,
        [id]: serializeSeriesPart(core),
      });
      this.state = { ...this.state, ...(core as any) };
    }

    this.seriesReady = true; this.dirtySeries = false; this.emit();
  }

  /** 상태 업데이트 (colors는 머지) */
  setData(patchOrFn: Partial<AppConfig> | ((prev: AppConfig) => Partial<AppConfig>)) {
    const patch = typeof patchOrFn === 'function' ? patchOrFn(this.state) : patchOrFn;
    const next: AppConfig = {
      ...this.state,
      ...patch,
      colors: (patch as any)?.colors ? { ...this.state.colors, ...(patch as any).colors } : this.state.colors,
      // 🔸 configVersion 제거
    };
    this.state = next;

    this.dirtyCommon = true;
    // Prevent persisting series defaults before async series hydration completes.
    if (this.seriesId && this.seriesReady) this.dirtySeries = true;

    this.emit();

    // 비동기 저장
    queueMicrotask(() => { this.flushSaves().catch(() => {/* ignore */}); });
  }

  setVolatileData(patch: Partial<EtcConfig>) {
    this.state = { ...this.state, ...(patch as Partial<AppConfig>) };
    this.emit();
  }

  getColors(key: string) { return (this.state as any)?.colors?.[key]; }

  private async flushSaves() {
    if (this.dirtyCommon) {
      try {
        const commonFile = await this.driver.readCommon();
        const toSave = splitCommonForSave(this.state); // 🔸 버전 값 없음
        await this.driver.writeCommon({ ...commonFile, ...toSave });
        this.dirtyCommon = false;
      } catch {
        if (this.driver.name === 'electron') this.setDriver(hasLocalStorage() ? WebDriver : MemoryDriver);
      }
    }
    if (this.dirtySeries && this.seriesId && this.seriesReady) {
      try {
        const seriesFile = await this.driver.readSeries();
        const toSaveNode = splitSeriesForSave(this.state).series;
        await this.driver.writeSeries({ ...seriesFile, [this.seriesId]: toSaveNode });
        this.dirtySeries = false;
      } catch {
        if (this.driver.name === 'electron') this.setDriver(hasLocalStorage() ? WebDriver : MemoryDriver);
      }
    }
  }
}

function hasLocalStorage() {
  try { return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'; } catch { return false; }
}

/** 싱글턴 */
export const configStore = new ConfigStore();
